import { useState } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Money, Variance } from "./badges";
import { Search } from "lucide-react";

const SECTIONS = ["EARNINGS", "DEDUCTIONS", "TAXES", "MILEAGE"];
const SECTION_LABEL = { EARNINGS: "Earnings", DEDUCTIONS: "Deductions", TAXES: "Taxes", MILEAGE: "Mileage" };

export function MovementsTable({ movements = [], ccy, showEmployee = false, testid = "movements-table" }) {
  const [active, setActive] = useState("ALL");
  const [q, setQ] = useState("");
  if (!movements.length)
    return (
      <div className="rounded-md border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
        No reportable movements between these two periods.
      </div>
    );
  const sections = SECTIONS;
  const query = q.trim().toLowerCase();
  const rows = movements.filter((m) => {
    if (active !== "ALL" && m.section !== active) return false;
    if (!query) return true;
    return [m.employee_name, m.field, m.state_code, m.file_number, SECTION_LABEL[m.section] || m.section]
      .some((v) => v && String(v).toLowerCase().includes(query));
  });
  return (
    <div data-testid={testid}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {["ALL", ...sections].map((s) => (
            <button
              key={s}
              data-testid={`mv-filter-${s.toLowerCase()}`}
              onClick={() => setActive(s)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                active === s
                  ? "border-foreground bg-foreground text-background"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {s === "ALL" ? "All" : SECTION_LABEL[s] || s}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-64">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="mv-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={showEmployee ? "Search name, code, field…" : "Search code or field…"}
            className="w-full rounded-full border border-border bg-card py-1.5 pl-8 pr-3 text-sm outline-none focus:border-foreground/40"
          />
        </div>
      </div>
      <div className="rounded-md border border-border bg-card overflow-auto max-h-[36rem]">
        <Table className="tbl-sticky">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Section</TableHead>
              {showEmployee && <TableHead>Employee</TableHead>}
              <TableHead>Code / Field</TableHead>
              <TableHead className="text-right">Current</TableHead>
              <TableHead className="text-right">Previous</TableHead>
              <TableHead className="text-right">Difference</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((m) => (
              <TableRow key={`${m.section}|${m.field}|${m.file_number ?? ""}|${m.state_code ?? ""}`} className="hover:bg-muted/40">
                <TableCell className="text-xs uppercase tracking-wide text-muted-foreground">
                  {SECTION_LABEL[m.section] || m.section}
                </TableCell>
                {showEmployee && (
                  <TableCell className="text-sm">
                    {m.employee_name}
                    {m.file_number && <span className="ml-1 font-mono text-xs text-muted-foreground">#{m.file_number}</span>}
                  </TableCell>
                )}
                <TableCell className="font-medium">
                  {m.field}
                  {m.state_code && <span className="ml-1 font-mono text-xs text-muted-foreground">{m.state_code}</span>}
                </TableCell>
                <TableCell className="text-right"><Money value={m.current_amount} ccy={ccy} /></TableCell>
                <TableCell className="text-right text-muted-foreground"><Money value={m.previous_amount} ccy={ccy} /></TableCell>
                <TableCell className="text-right"><Variance delta={m.difference} /></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showEmployee ? 6 : 5} className="py-8 text-center text-sm text-muted-foreground">
                  No movements match your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
